import React, { FC, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";

import { Button, IButtonProps } from "@/components/Button";
import { Text } from "@/components/Text";
import { Flex } from "@/eworkitComponents";
import { Actions } from "@/views/components/Actions";

import { openShareWindow, shareTargets, TShareTarget } from "./service";
import { ShareButtonOption } from "./ShareButtonOption";

import "./_share-button.scss";

interface IShareButtonProps extends IButtonProps {
  url: string;
  showLabel?: boolean;
  onShared?: (method: TShareTarget | "link") => void;
}

export const ShareButton: FC<IShareButtonProps> = ({ url, icon, showLabel, onShared, ...buttonProps }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslation("common");

  const baseClass = "share-button";

  const handleItemClick = useCallback(
    (target: TShareTarget) => {
      openShareWindow(target, url);
      onShared?.(target);
    },
    [url, onShared],
  );

  const handleCopyLinkClick = useCallback(() => {
    if (!inputRef.current) return;

    inputRef.current.select();
    document.execCommand("copy");

    onShared?.("link");
  }, [onShared]);

  const renderTarget = () => {
    return (
      <Button icon={icon || "share-1"} {...buttonProps}>
        {showLabel && <Text size={buttonProps.size || "md"}>{t("COMPONENTS.SHARE.SHARE")}</Text>}
      </Button>
    );
  };

  return (
    <Actions
      className={baseClass}
      placement="bottom-start"
      renderActiveBar={false}
      renderTarget={renderTarget}
      usePortal>
      <Flex className={`${baseClass}__dropdown`} direction="column" padding={["xs", false]} align="center">
        {shareTargets.map(target => (
          <ShareButtonOption
            key={target}
            icon={target}
            text={t(`COMPONENTS.SHARE.SHARE_ON_${target.toUpperCase()}`)}
            onClick={() => handleItemClick(target)}
          />
        ))}

        <Flex width={12}>
          <ShareButtonOption icon="link" text={t(`COMPONENTS.SHARE.COPY_LINK`)} onClick={handleCopyLinkClick} />
          <input className={`${baseClass}__copy-input`} ref={inputRef} value={url} />
        </Flex>
      </Flex>
    </Actions>
  );
};
